import * as RadixDialog from '@radix-ui/react-dialog';
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { useBreakpoint } from '@/shared/lib/hooks';
import { Button } from './Button';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Hide the close affordances for a dialog the user must answer. */
  dismissible?: boolean;
}

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };

/**
 * One dialog for the whole app. Radix gives us the focus trap, Escape handling, scroll lock and
 * focus restoration the hand-rolled legacy modals never had.
 *
 * On mobile it becomes a bottom sheet — a centred dialog at 375px wastes most of the screen.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
}: DialogProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  /*
   * Escape must close the dialog even when it is pressed the instant the dialog appears.
   *
   * Radix binds its own Escape handling when the content mounts. A dialog opened from a menu
   * item mounts while the menu is still tearing down, and a keypress that lands in that gap is
   * swallowed: measured, two out of three presses left the dialog open. Users hit Escape again
   * and it works, which is exactly the kind of "sometimes" bug that never gets reported
   * properly. A capture-phase listener on the document closes the gap; Radix still handles the
   * normal case, and closing twice is harmless.
   */
  useEffect(() => {
    if (!open || !dismissible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Only the topmost dialog responds, so a nested one does not close its parent too.
      const dialogs = document.querySelectorAll('[role="dialog"]');
      const target = event.target as Node | null;
      const topmost = dialogs[dialogs.length - 1];
      if (dialogs.length > 1 && target && topmost && !topmost.contains(target)) return;
      onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, dismissible, onOpenChange]);

  return (
    <RadixDialog.Root open={open} onOpenChange={dismissible ? onOpenChange : undefined}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 animate-fade-in bg-black/60 backdrop-blur-[2px]" />
        <RadixDialog.Content
          /*
           * Radix's own default puts focus on the first focusable element in the content, which
           * is always the close button — it is rendered before `children` in the markup below,
           * so a field the caller marked `autoFocus` never won that race regardless of the order
           * effects run in. React's `autoFocus` calls `.focus()` imperatively rather than
           * reflecting an `autofocus` DOM attribute, so it cannot be queried for; a field that
           * wants this has to also carry `data-autofocus`, which every one of them now does.
           */
          onOpenAutoFocus={(event) => {
            const marked = (event.currentTarget as HTMLElement).querySelector<HTMLElement>('[data-autofocus]');
            if (marked) {
              event.preventDefault();
              marked.focus();
            }
          }}
          onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
          onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
          className={cn(
            'fixed z-50 flex flex-col border border-border bg-surface shadow-lg focus:outline-none',
            isMobile
              ? 'inset-x-0 bottom-0 max-h-[92vh] animate-slide-in-bottom rounded-t-xl'
              : cn(
                  // Centred with auto margins, not `-translate-x/y-1/2`: the entrance animation
                  // sets `transform`, which replaces the element's own for as long as it runs, so
                  // a translate-centred dialog was drawn with its corner at the middle of the
                  // screen and then snapped into place when the animation ended.
                  'inset-0 m-auto h-fit w-[calc(100vw-2rem)]',
                  'max-h-[88vh] animate-slide-up rounded-xl',
                  SIZES[size],
                ),
          )}
        >
          {isMobile && (
            <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border-strong" aria-hidden />
          )}
          <div className="flex items-start justify-between gap-4 px-5 pb-3 pt-4">
            <div className="min-w-0">
              <RadixDialog.Title className="text-md font-semibold">{title}</RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-1 text-sm text-fg-muted">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            {dismissible && (
              <RadixDialog.Close asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t('a11y.closeDialog')}>
                  <X className="h-4 w-4" />
                </Button>
              </RadixDialog.Close>
            )}
          </div>

          {children && <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">{children}</div>}

          {footer && (
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
}

/** Confirmation for anything irreversible. The body must say what will actually happen. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive,
  loading,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            loading={loading}
            onClick={() => void onConfirm()}
          >
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    >
      {body && <div className="text-base text-fg-muted">{body}</div>}
    </Dialog>
  );
}
